import { ChevronRight } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import TableOfContents from "@/components/TableOfContents";

/**
 * /methode — Reference page about HorsIndex's working method.
 *
 * Roles distribution (clear separation with /services):
 *   /services → what HorsIndex tries to obtain (results)
 *   /methode  → how HorsIndex proceeds (this page)
 *
 * Consolidated structure (5 sections, no redundancy):
 *   01. Pourquoi une méthode dédiée ?
 *   02. Le parcours d'une donnée
 *   03. Les quatre étapes
 *   04. Les écueils les plus courants
 *   05. Ce que cela change concrètement
 * + PageLayout footer CTA — Demander un audit initial
 */

const FLOW = [
  { eyebrow: "Origine", title: "Registre officiel" },
  { eyebrow: "Mise à disposition", title: "Base open data" },
  { eyebrow: "Reprise", title: "Agrégateur privé" },
  { eyebrow: "Visibilité", title: "Moteur de recherche" },
  { eyebrow: "Propagation", title: "Rediffusions secondaires" },
];

const STEPS = [
  {
    title: "Cartographier",
    desc: "Identifier les sources visibles, les plateformes concernées et les rediffusions observables.",
  },
  {
    title: "Qualifier",
    desc: "Distinguer la nature des données, leur origine et les leviers juridiques mobilisables.",
  },
  {
    title: "Agir",
    desc: "Préparer les demandes adaptées : rectification, opposition, effacement, désindexation.",
  },
  {
    title: "Documenter",
    desc: "Organiser preuves, URL, captures, dates et historique pour structurer le dossier dans la durée.",
  },
];

const PITFALLS = [
  {
    title: "Mauvais interlocuteur",
    desc: "Une demande adressée à un acteur qui ne maîtrise pas réellement la source du traitement reste sans effet.",
  },
  {
    title: "Fondement mal choisi",
    desc: "Accès, opposition, effacement, rectification ou désindexation n'ouvrent pas les mêmes effets juridiques.",
  },
  {
    title: "Absence de preuves",
    desc: "Sans URL, captures, dates et historique, la situation reste difficile à qualifier.",
  },
  {
    title: "Attentes irréalistes",
    desc: "Toutes les données publiques ne peuvent pas disparaître ; certaines reprises peuvent néanmoins être corrigées ou limitées.",
  },
];

const PRODUITS = [
  {
    title: "Reprendre la main",
    desc: "Passer d'une exposition subie à une situation identifiée, organisée et documentée.",
  },
  {
    title: "Cibler les sources",
    desc: "Repérer les registres, plateformes, moteurs de recherche et rediffusions réellement concernés.",
  },
  {
    title: "Choisir les bons leviers",
    desc: "Distinguer les demandes utiles des démarches sans effet réel.",
  },
  {
    title: "Préparer l'action",
    desc: "Rassembler les éléments nécessaires avant toute demande ou intervention.",
  },
  {
    title: "Suivre les réponses",
    desc: "Conserver une trace des démarches engagées, des réponses reçues et des suites possibles.",
  },
  {
    title: "Passer le relais si nécessaire",
    desc: "Identifier les situations nécessitant l'intervention d'un avocat ou d'un professionnel spécialisé.",
  },
];

const FACTEURS = [
  {
    title: "Résultat visible dans un moteur de recherche",
    desc: "L'information apparaît directement dans les résultats d'une recherche nominative, avant même toute visite du site source.",
  },
  {
    title: "Page publique accessible sans compte",
    desc: "La page est consultable par toute personne, sans authentification ni justification particulière.",
  },
  {
    title: "Document téléchargeable",
    desc: "Un fichier contenant des données personnelles peut être copié, conservé et remis en circulation sans contrôle.",
  },
  {
    title: "Cartographie, adresse ou mise en relation",
    desc: "Localisations, adresses, liens capitalistiques ou relations entre personnes et entités sont représentés et croisables.",
  },
  {
    title: "Espace accessible avec un compte gratuit",
    desc: "L'accès suppose une inscription, mais celle-ci reste ouverte à tous et ne constitue pas un réel filtre.",
  },
  {
    title: "Espace professionnel payant ou authentifié",
    desc: "La consultation est réservée à des utilisateurs identifiés, ce qui réduit l'accessibilité générale sans neutraliser les règles applicables.",
  },
  {
    title: "Base interne non publiquement accessible",
    desc: "La donnée est détenue et traitée sans mise à disposition du public.",
  },
];

const TOC_ITEMS = [
  { id: "m-pourquoi", label: "Pourquoi une méthode dédiée" },
  { id: "m-parcours", label: "Parcours d'une donnée" },
  { id: "m-grille", label: "Grille d'analyse de l'exposition" },
  { id: "m-etapes", label: "Les quatre étapes" },
  { id: "m-eviter", label: "Les écueils les plus courants" },
  { id: "m-produit", label: "Ce que cela change" },
];

function SectionEyebrow({ n, label }) {
  return (
    <p className="label-eyebrow mb-6">
      <span className="text-[#B83A34]">{String(n).padStart(2, "0")}</span>
      {" / "}
      {label}
    </p>
  );
}

function FlowDiagram() {
  return (
    <div data-testid="methode-flow" className="mt-12 lg:mt-16">
      <ol className="hidden lg:flex items-stretch gap-0">
        {FLOW.map((s, i) => (
          <li
            key={s.title}
            data-testid={`methode-flow-step-${i}`}
            className="flex-1 flex items-stretch min-w-0"
          >
            <div className="flex-1 min-w-0 bg-white border border-[#E2DEDA] rounded-md p-6 flex flex-col">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-3">
                {s.eyebrow}
              </span>
              <h3 className="font-serif text-lg text-[#181A1D] font-normal leading-snug tracking-tight text-balance">
                {s.title}
              </h3>
            </div>
            {i < FLOW.length - 1 ? (
              <div
                aria-hidden="true"
                className="flex items-center justify-center px-2 text-[#B4B8BD]"
              >
                <ChevronRight size={20} strokeWidth={1.2} />
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      <ol className="lg:hidden flex flex-col gap-3">
        {FLOW.map((s, i) => (
          <li key={`m-${s.title}`} className="flex flex-col items-stretch">
            <div className="bg-white border border-[#E2DEDA] rounded-md p-6">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95]">
                {s.eyebrow}
              </span>
              <h3 className="font-serif text-xl text-[#181A1D] font-normal leading-snug tracking-tight mt-2 text-balance">
                {s.title}
              </h3>
            </div>
            {i < FLOW.length - 1 ? (
              <div
                aria-hidden="true"
                className="flex items-center justify-center py-1 text-[#B4B8BD]"
              >
                <svg width="2" height="22" viewBox="0 0 2 22">
                  <line
                    x1="1"
                    y1="0"
                    x2="1"
                    y2="22"
                    stroke="currentColor"
                    strokeWidth="1"
                  />
                </svg>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Methode() {
  return (
    <PageLayout
      eyebrow="Méthode"
      title={<>Comment HorsIndex analyse et réduit votre <em className="italic font-normal">exposition</em> en ligne.</>}
      pattern="sequence"
      intro="HorsIndex organise les situations d'exposition numérique en plusieurs étapes : cartographier les sources visibles, cibler les bons interlocuteurs, préparer les démarches utiles et suivre les échanges dans la durée. Un cadre de travail discipliné, fondé sur le cadre européen de protection des données, pensé pour éviter les actions dispersées et donner à une situation parfois confuse une lecture plus nette."
    >
      <TableOfContents items={TOC_ITEMS} />

      {/* 01 — Pourquoi une méthode */}
      <section
        id="m-pourquoi"
        data-testid="methode-section-01"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionEyebrow n={1} label="Constat" />
            <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
              Pourquoi une méthode dédiée&nbsp;?
            </h2>
            <div className="h-[2px] w-10 bg-[#E94B3C] mt-10" />
          </div>
          <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
            <p>
              L'exposition numérique ne provient pas d'une seule source. Une
              même information peut être publiée dans un registre officiel,
              reprise dans une base open data, rediffusée par plusieurs
              plateformes privées, indexée par les moteurs de recherche puis
              recopiée ailleurs.
            </p>
            <p>
              Agir efficacement suppose donc de comprendre d'abord la
              circulation de la donnée. Sans cartographie préalable, les
              démarches risquent d'être adressées au mauvais acteur, sur un
              fondement inadéquat ou avec une demande impossible à satisfaire.
            </p>
          </div>
        </div>
      </section>

      {/* 02 — Parcours d'une donnée */}
      <section
        id="m-parcours"
        data-testid="methode-section-02"
        className="bg-[#F2F0ED] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow n={2} label="Circulation" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Comment une information circule de site en site.
              </h2>
            </div>
          </div>
          <FlowDiagram />
        </div>
      </section>

      {/* 03 — Grille d'analyse de l'exposition */}
      <section
        id="m-grille"
        data-testid="methode-section-grille"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={3} label="Grille" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Ce que HorsIndex examine pour chaque situation.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Sept facteurs pour évaluer le niveau concret d'exposition
                d'une donnée : son accessibilité, sa visibilité, la
                facilité avec laquelle elle peut être recherchée, copiée,
                croisée ou réutilisée. Cette lecture aide à ordonner les
                priorités d'une démarche.
              </p>
            </div>
          </div>

          <ol data-testid="methode-facteurs" className="border-t border-[#E2DEDA]">
            {FACTEURS.map((f, i) => (
              <li
                key={f.title}
                data-testid={`methode-facteur-${i}`}
                className="border-b border-[#E2DEDA] py-6 lg:py-7 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-baseline"
              >
                <span className="md:col-span-2 font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34]">
                  Facteur {String(i + 1).padStart(2, "0")} / 07
                </span>
                <h3 className="md:col-span-4 font-serif text-xl lg:text-[22px] text-[#181A1D] font-normal leading-snug tracking-tight text-balance">
                  {f.title}
                </h3>
                <p className="md:col-span-6 text-[14px] lg:text-[15px] text-[#5A5E64] font-normal leading-relaxed text-pretty">
                  {f.desc}
                </p>
              </li>
            ))}
          </ol>

          <aside
            data-testid="methode-grille-note"
            className="mt-10 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8 max-w-4xl"
          >
            <p className="label-eyebrow text-[#666A70] mb-3">Précisions</p>
            <div className="space-y-4 text-[13px] lg:text-sm text-[#5A5E64] font-normal leading-relaxed text-pretty">
              <p>
                Cette grille ne constitue pas une hiérarchie juridique et
                ne détermine pas, à elle seule, la légalité d'un
                traitement. Elle sert à évaluer le niveau concret
                d'exposition, la facilité d'accès, les risques de
                réutilisation et l'ordre de priorité des démarches.
              </p>
              <p>
                Plusieurs facteurs se cumulent souvent : une même page
                publique peut contenir un document téléchargeable, être
                indexée par les moteurs de recherche et inclure une
                cartographie. Enfin, un espace payant ou authentifié
                n'est pas automatiquement conforme ni nécessairement
                moins problématique : le RGPD et les autres règles
                applicables concernent également les espaces réservés.
                HorsIndex concentre simplement son action prioritaire sur
                la partie publiquement accessible.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* 04 — Les quatre étapes */}
      <section
        id="m-etapes"
        data-testid="methode-section-03"
        className="bg-[#F2F0ED] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={4} label="Étapes" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Les quatre étapes.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Quatre opérations articulées dans cet ordre. Aucune ne peut
                être court-circuitée sans fragiliser les suivantes.
              </p>
            </div>
          </div>
          <ol
            data-testid="methode-steps"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12"
          >
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`methode-step-${i}`}
                className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
              >
                <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
                  Étape {String(i + 1).padStart(2, "0")} / 04
                </span>
                <h3 className="font-serif text-2xl text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
                  {s.title}
                </h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
                  {s.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 05 — Ce que la méthode permet d'éviter */}
      <section
        id="m-eviter"
        data-testid="methode-section-04"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={5} label="Écueils" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Les écueils les plus courants.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Quatre erreurs fréquentes lorsqu'une démarche est engagée
                sans qualification préalable. Un travail préparatoire
                rigoureux existe précisément pour les éviter.
              </p>
            </div>
          </div>
          <ul
            data-testid="methode-pitfalls"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12"
          >
            {PITFALLS.map((p, i) => (
              <li
                key={p.title}
                data-testid={`methode-pitfall-${i}`}
                className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
              >
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70] mb-4">
                  Écueil {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-xl text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
                  {p.title}
                </h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
                  {p.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 06 — Ce que permet la méthode */}
      <section
        id="m-produit"
        data-testid="methode-section-05"
        className="bg-[#F2F0ED]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={6} label="Ce que cela change" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Ce que cela change concrètement.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Une démarche organisée aide à rendre une situation
                d'exposition lisible et traitable, à cibler les bons
                interlocuteurs et à choisir des leviers adaptés, plutôt
                qu'à multiplier des actions isolées, imprécises ou sans
                effet réel.
              </p>
            </div>
          </div>

          <ul
            data-testid="methode-produits"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12"
          >
            {PRODUITS.map((p, i) => (
              <li
                key={p.title}
                data-testid={`methode-produit-${i}`}
                className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
              >
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70] mb-4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-xl text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
                  {p.title}
                </h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
                  {p.desc}
                </p>
              </li>
            ))}
          </ul>

          <p
            data-testid="methode-produits-note"
            className="mt-10 max-w-3xl text-[13px] italic text-[#666A70] font-normal leading-relaxed"
          >
            Le travail engagé ne repose pas sur des modèles standardisés.
            Il vise avant tout à organiser la situation, cibler les bons
            acteurs et préparer des démarches adaptées au contexte réel.
          </p>
        </div>
      </section>
    </PageLayout>
  );
}
